import { FC, useState } from 'react';
import { toast } from 'sonner';
import { Typography, Button, ChevronDown, Copy } from '@dreamteam-monorepo/ui';
import { BELeague } from '@/lib/leagueMappers';

interface JoinLeagueCardProps {
  league: BELeague;
  onJoin: () => void;
  joining?: boolean;
  defaultExpanded?: boolean;
}

const Divider: FC = () => <hr className="border-t border-[--color-grey-200] w-full" />;

export const JoinLeagueCard: FC<JoinLeagueCardProps> = ({
  league,
  onJoin,
  joining = false,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(league.join_code).then(() => {
      toast.success('Code copied!');
    });
  };

  return (
    <div className="bg-white rounded-[--corner-md] shadow-[0px_4px_24px_rgba(34,37,38,0.12)] flex flex-col p-[--padding-lg]">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => !defaultExpanded && setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-[--padding-sm] text-left"
        aria-expanded={expanded}
      >
        <div className="flex flex-col gap-[--padding-sm] flex-1 min-w-0">
          <Typography
            as="span"
            variant="headline030"
            className="text-[--color-neutral-900] truncate"
          >
            {league.name}
          </Typography>
          <div className="flex flex-wrap items-center gap-x-[--padding-lg] gap-y-[--padding-xs]">
            <span className="bg-[#eae9fd] px-[--padding-xs] py-[--padding-3xs] rounded-[--corner-xs]">
              <Typography variant="label020" className="text-[--color-neutral-900]">
                {league.is_public ? 'Public' : 'Private'}
              </Typography>
            </span>
            <div className="flex items-center gap-[--padding-xs]">
              <Typography variant="paragraph010" className="text-[--color-neutral-900]">
                Start:
              </Typography>
              <Typography variant="paragraph010" className="text-[--color-neutral-900] !font-bold">
                GW {league.start_gameweek || 1}
              </Typography>
            </div>
            <div className="flex items-center gap-[--padding-xs]">
              <Typography variant="paragraph010" className="text-[--color-neutral-900]">
                Teams:
              </Typography>
              <Typography variant="paragraph010" className="text-[--color-neutral-900] !font-bold">
                {league.member_count}
              </Typography>
            </div>
          </div>
        </div>
        {!defaultExpanded && (
          <ChevronDown
            className={[
              'size-6 text-[--color-neutral-600] shrink-0',
              'transition-transform duration-300 ease-in-out',
              expanded ? 'rotate-180' : '',
            ].join(' ')}
          />
        )}
      </button>

      {/* Animated expand/collapse — grid-rows transition avoids max-height hacks */}
      <div
        className={[
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        ].join(' ')}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-[--padding-md] mt-[--padding-md]">
            <Divider />

            {league.commissioner_username && (
              <>
                <div className="flex items-center gap-[--padding-xs]">
                  <Typography variant="paragraph010" className="text-[--color-neutral-900]">
                    Chairman:
                  </Typography>
                  <Typography
                    variant="paragraph010"
                    className="text-[--color-neutral-900] !font-bold"
                  >
                    {league.commissioner_username}
                  </Typography>
                </div>
                <Divider />
              </>
            )}

            <div className="flex items-center gap-[--padding-md]">
              <div className="flex items-center gap-[--padding-xs] flex-1 min-w-0">
                <Typography variant="paragraph010" className="text-[--color-neutral-900] shrink-0">
                  League Code:
                </Typography>
                <Typography
                  variant="paragraph010"
                  className="text-[--color-neutral-900] !font-bold truncate"
                >
                  {league.join_code}
                </Typography>
              </div>
              <button
                type="button"
                onClick={handleCopyCode}
                className="flex items-center gap-1 shrink-0 text-[--color-blue-200]"
              >
                <Typography variant="button020" className="text-[--color-blue-200]">
                  Copy Code
                </Typography>
                <Copy className="size-5" />
              </button>
            </div>

            <Divider />

            <div className="flex items-end gap-[--padding-md]">
              {league.description && (
                <div className="flex flex-col gap-[--padding-2xs] flex-1 min-w-0">
                  <Typography variant="paragraph010" className="text-[--color-neutral-900]">
                    Description:
                  </Typography>
                  <Typography
                    variant="paragraph010"
                    className="text-[--color-neutral-900] !font-bold"
                  >
                    {league.description}
                  </Typography>
                </div>
              )}
              <Button
                variant="contained"
                color="blue"
                size="small"
                onClick={onJoin}
                loading={joining}
                disabled={joining}
                className="shrink-0 ml-auto"
              >
                Join League
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
